import { type AudienceData, AudienceOption } from '@openx/types/targeting/audience.ts';
import { ComparisonType } from '@openx/types/targeting/targetingValuesTypes.ts';
import omit from 'lodash/omit';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTargetingContext } from '../../../utils/context/TargetingContext.tsx';
import { AudienceItemWithStatus } from '../components/AudienceItemWithStatus.tsx';
import { OaAudienceItem } from '../components/OaAudienceItem.tsx';
import { MatchedUsersOption } from '../constants.ts';
import { getAudienceTypeLabelsMap } from '../utils/getAudienceTypeLabelsMap.ts';
import { getMatchedUsersLabelsMap } from '../utils/getMatchedUsersLabelsMap.ts';

type UseAudienceItemsParams = {
  targetingParams: AudienceData | null;
  accountUid: string;
  readonly?: boolean;
  isFromCurated?: boolean;
};

export const useAudienceItems = ({
  targetingParams,
  accountUid,
  readonly,
  isFromCurated = false,
}: UseAudienceItemsParams) => {
  const { t } = useTranslation();
  const { isOa, useAudienceOptionsDetailsFetch } = useTargetingContext();

  const selectedAudience = omit(targetingParams, [AudienceOption.INTENDED_AUDIENCE]);
  const selectedAudienceType = targetingParams?.[AudienceOption.INTENDED_AUDIENCE];
  const selectedTrafficFilterEids = selectedAudience[AudienceOption.TRAFFIC_FILTER_EIDS];
  const selectedTrafficFilterEidsArray = useMemo(
    () => [...(selectedTrafficFilterEids || [])],
    [selectedTrafficFilterEids]
  );

  const hasSelectedEids = useMemo(() => selectedTrafficFilterEidsArray.length > 0, [selectedTrafficFilterEidsArray]);

  const selectedAudienceOption = useMemo(() => {
    if (!selectedAudience || Object.keys(selectedAudience).length === 0) return null;

    if (selectedAudience[AudienceOption.MATCHED_USERS]) return AudienceOption.MATCHED_USERS;

    if (selectedAudience[AudienceOption.TRAFFIC_FILTER_EIDS]) return AudienceOption.MATCHED_USERS;

    const firstNonEidsKey = Object.keys(selectedAudience).find(k => k !== AudienceOption.TRAFFIC_FILTER_EIDS);
    return (firstNonEidsKey ?? Object.keys(selectedAudience)[0]) as AudienceOption;
  }, [selectedAudience]);

  const getSelectedItems = () => {
    if (selectedAudienceOption && selectedAudience?.[selectedAudienceOption]) {
      const selectedOption = selectedAudience[selectedAudienceOption];
      const val = selectedOption?.val;

      if (val) {
        return val.split(',');
      }

      if (typeof selectedOption === 'string') {
        return [selectedOption];
      }
    }

    if (selectedAudienceOption === AudienceOption.MATCHED_USERS && hasSelectedEids) {
      return [MatchedUsersOption.MatchedByEids];
    }

    return [];
  };

  const { options, isLoading, refetch } = useAudienceOptionsDetailsFetch(
    accountUid,
    selectedAudienceOption,
    getSelectedItems()
  );

  const audienceTypeLabels = useMemo(() => getAudienceTypeLabelsMap(t), [t]);

  useEffect(() => {
    if (refetch && selectedAudienceOption !== AudienceOption.LIVEINTENT) {
      refetch();
    }
  }, [refetch, selectedAudienceOption]);

  const optionsMap = useMemo(() => {
    if (selectedAudienceOption === AudienceOption.MATCHED_USERS) {
      return getMatchedUsersLabelsMap(t);
    }
    if (selectedAudienceOption && selectedAudienceOption !== AudienceOption.LIVEINTENT) {
      return options;
    }

    return undefined;
  }, [options, t, selectedAudienceOption]);

  const filtersTitle = useMemo(
    () => (selectedAudienceOption !== AudienceOption.LIVEINTENT ? t('Included') : t('Equals')),
    [selectedAudienceOption, t]
  );

  const allAnySwitchState = useMemo(
    () =>
      selectedAudienceOption ? selectedAudience?.[selectedAudienceOption]?.op !== ComparisonType.INVERSE_IN : false,
    [selectedAudience, selectedAudienceOption]
  );

  const DefaultItemComponent = useMemo(() => {
    let component = selectedAudienceOption === 'openaudience_custom' ? AudienceItemWithStatus : undefined;
    component = readonly && isOa ? OaAudienceItem : component;

    return component;
  }, [readonly, isOa, selectedAudienceOption]);

  const isReadOnly = readonly ?? true;

  const isMinimalAudiencePreview = readonly && isOa && isFromCurated;

  return {
    selectedAudience,
    selectedAudienceType,
    selectedTrafficFilterEidsArray,
    selectedAudienceOption,
    getSelectedItems,
    options,
    isLoading,
    audienceTypeLabels,
    optionsMap,
    filtersTitle,
    allAnySwitchState,
    DefaultItemComponent,
    isReadOnly,
    isMinimalAudiencePreview,
  };
};
